import { getAiInput, getAiProfile } from './ai'
import { createCanonicalTrack } from './canonical-track'
import { beginReload, resolveProjectileHits, stepCombatTimers, stepProjectiles, tryFire } from './combat'
import { add, clamp, distance, dot, scale, subtract } from './math'
import { stepOffTrackState } from './offtrack'
import { advanceCheckpoint, createRaceState, rankRacers, stepRaceClock } from './race'
import { crossesCheckpointGate, getCheckpointSegmentProgress } from './race-position'
import { getStartingGridPose } from './starting-grid'
import { getTrackSample, projectToTrack, projectToTrackLocal } from './track'
import { TRACK_CHECKPOINT_COUNT } from './track-data'
import type {
  ControlInput,
  SimulationEvent,
  SimulationEventType,
  SimulationState,
  Track,
  Vec2,
  VehicleState,
} from './types'
import {
  createVehicle,
  EMPTY_INPUT,
  getCleanCheckpointBoost,
  resolveVehiclePackCollisions,
  stepVehicle,
  VEHICLE_CONTACT_DISTANCE,
} from './vehicle'

const VEHICLE_IDS = ['player', 'rival-1', 'rival-2', 'rival-3', 'rival-4', 'rival-5']
export interface SimulationGridEntry {
  id: string
  kind: VehicleState['kind']
  displayName?: string
}

export function getStartingGridHeading(track: Track): number {
  return getStartingGridPose(track, 0).heading
}

function spawnVehicle(
  track: Track,
  id: string,
  index: number,
  kind: VehicleState['kind'] = id === 'player' ? 'player' : 'ai',
  displayName?: string,
): VehicleState {
  const pose = getStartingGridPose(track, index)
  return {
    ...createVehicle(id, kind, pose.position, pose.heading),
    displayName,
    trackSampleIndex: pose.sampleIndex,
    weaponCooldown: 0,
    fireSuppressionTimer: id === 'player' ? 0 : 2.5 + index * 0.45,
  }
}

function surfaceFor(track: Track, vehicle: VehicleState) {
  const projection = projectToTrackLocal(track, vehicle.position, vehicle.trackSampleIndex)
  const sample = getTrackSample(track, projection.sampleIndex)
  return {
    projection,
    surface: {
      lateral: projection.lateral,
      trackWidth: track.width,
      recoveryDirection: scale(sample.normal, projection.lateral === 0 ? 0 : -Math.sign(projection.lateral)),
    },
  }
}

function keepAiOnTrack(vehicle: VehicleState, track: Track): VehicleState {
  if (vehicle.kind !== 'ai') return vehicle
  const projection = projectToTrackLocal(track, vehicle.position, vehicle.trackSampleIndex)
  const centerLimit = track.width * 0.78
  if (Math.abs(projection.lateral) <= centerLimit) return vehicle
  const sample = getTrackSample(track, projection.sampleIndex)
  const boundedLateral = Math.sign(projection.lateral) * centerLimit
  const position = add(vehicle.position, scale(sample.normal, boundedLateral - projection.lateral))
  const outward = scale(sample.normal, Math.sign(projection.lateral))
  const outwardSpeed = Math.max(0, dot(vehicle.velocity, outward))
  const velocity = subtract(vehicle.velocity, scale(outward, outwardSpeed))
  return {
    ...vehicle,
    position,
    velocity,
    speed: dot(velocity, sample.tangent),
    trackSampleIndex: projection.sampleIndex,
  }
}

function addEvent(
  events: SimulationEvent[],
  nextId: number,
  type: SimulationEventType,
  vehicleId: string,
  targetId?: string,
  value?: number,
  position?: { x: number; y: number },
): number {
  events.push({ id: nextId, type, vehicleId, targetId, value, position })
  return nextId + 1
}

export function createSimulation(): SimulationState {
  return createSimulationWithGrid(VEHICLE_IDS.map((id) => ({ id, kind: id === 'player' ? 'player' : 'ai' })))
}

export function createSimulationWithGrid(
  grid: SimulationGridEntry[],
  classificationAuthority: SimulationState['classificationAuthority'] = 'local',
): SimulationState {
  const track = createCanonicalTrack()
  const safeGrid = grid.slice(0, 8)
  const vehicles = safeGrid.map((entry, index) => spawnVehicle(track, entry.id, index, entry.kind, entry.displayName))
  const vehicleIds = vehicles.map((vehicle) => vehicle.id)
  const playerId = vehicles.find((vehicle) => vehicle.kind === 'player')?.id ?? 'player'
  const race = createRaceState(vehicleIds, 3, playerId, TRACK_CHECKPOINT_COUNT)
  return {
    tick: 0,
    time: 0,
    classificationAuthority,
    track,
    vehicles,
    projectiles: [],
    race,
    ranking: [...vehicleIds],
    nextProjectileId: 1,
    nextEventId: 1,
    events: [],
    stats: { shotsFired: 0, hits: 0 },
  }
}

export function stepSimulation(state: SimulationState, playerInput: ControlInput, dt: number): SimulationState {
  const safeDt = Math.max(0, Math.min(dt, 0.1))
  const steppedClock = stepRaceClock(state.race, safeDt)
  let race =
    state.classificationAuthority === 'local'
      ? steppedClock
      : {
          ...steppedClock,
          phase: state.race.phase,
          finishCountdown:
            state.race.phase === 'finish-countdown' ? Math.max(0, steppedClock.finishCountdown ?? 0) : null,
        }
  const events: SimulationEvent[] = []
  let nextEventId = state.nextEventId
  let stats = state.stats
  if (race.phase === 'countdown') {
    return { ...state, tick: state.tick + 1, time: state.time + safeDt, race, events }
  }
  if (race.phase === 'finished') {
    return { ...state, tick: state.tick + 1, time: state.time + safeDt, race, events }
  }

  const previousPositions = new Map(state.vehicles.map((vehicle) => [vehicle.id, vehicle.position]))
  const drivenEndpoints = new Map<string, Vec2>()
  const invalidCheckpointSweeps = new Set<string>()
  const world = { vehicles: state.vehicles }
  const requestedInputs = new Map<string, ControlInput>()
  for (const vehicle of state.vehicles) {
    const progress = race.progress[vehicle.id]
    const input =
      progress?.recordStatus !== 'racing'
        ? EMPTY_INPUT
        : vehicle.kind === 'player'
          ? playerInput
          : vehicle.kind === 'remote'
            ? EMPTY_INPUT
            : getAiInput(vehicle, world, state.track, getAiProfile(vehicle.id))
    requestedInputs.set(vehicle.id, input)
  }

  const inputs = new Map<string, ControlInput>()
  let vehicles = state.vehicles.map((vehicle) => {
    const timed = stepCombatTimers(vehicle, safeDt)
    if (vehicle.reloadTimer > 0 && timed.reloadTimer === 0) {
      nextEventId = addEvent(events, nextEventId, 'reload-complete', vehicle.id)
    }
    const input = timed.driverStunTimer > 0 ? EMPTY_INPUT : (requestedInputs.get(vehicle.id) ?? EMPTY_INPUT)
    inputs.set(vehicle.id, input)
    const { projection, surface } = surfaceFor(state.track, timed)
    const driven = timed.kind === 'remote' ? timed : stepVehicle(timed, input, surface, safeDt)
    drivenEndpoints.set(vehicle.id, driven.position)
    if (!vehicle.drift.active && driven.drift.active) {
      nextEventId = addEvent(events, nextEventId, 'drift-start', vehicle.id)
    }
    if (!driven.drift.active && driven.boost > vehicle.boost) {
      nextEventId = addEvent(events, nextEventId, 'drift-bank', vehicle.id, undefined, driven.boost - vehicle.boost)
    }
    const stabilized = keepAiOnTrack(driven, state.track)
    if (stabilized.kind === 'remote') {
      const remoteProjection = projectToTrackLocal(state.track, stabilized.position, projection.sampleIndex)
      return { ...stabilized, trackSampleIndex: remoteProjection.sampleIndex }
    }
    const offTrack = stepOffTrackState(stabilized, state.track, safeDt, projection.lateral)
    if (offTrack.railBreakSide !== null) {
      const encodedRail = offTrack.railBreakSide * (offTrack.projectionSampleIndex + 1)
      nextEventId = addEvent(events, nextEventId, 'rail-break', vehicle.id, undefined, encodedRail)
    }
    if (offTrack.exploded) {
      invalidCheckpointSweeps.add(vehicle.id)
      nextEventId = addEvent(
        events,
        nextEventId,
        'vehicle-explode',
        vehicle.id,
        undefined,
        undefined,
        stabilized.position,
      )
    }
    if (!vehicle.boostActive && offTrack.vehicle.boostActive) {
      nextEventId = addEvent(events, nextEventId, 'boost', vehicle.id)
    }
    return offTrack.vehicle
  })
  for (let a = 0; a < vehicles.length; a += 1) {
    for (let b = a + 1; b < vehicles.length; b += 1) {
      const vehicleA = vehicles[a]
      const vehicleB = vehicles[b]
      if (!vehicleA || !vehicleB || distance(vehicleA.position, vehicleB.position) >= VEHICLE_CONTACT_DISTANCE) continue
      nextEventId = addEvent(events, nextEventId, 'vehicle-contact', vehicleA.id, vehicleB.id)
    }
  }
  vehicles = resolveVehiclePackCollisions(vehicles)

  let projectiles = state.projectiles
  let nextProjectileId = state.nextProjectileId
  vehicles = vehicles.map((vehicle) => {
    const input = inputs.get(vehicle.id) ?? EMPTY_INPUT
    let armedVehicle = input.reload ? beginReload(vehicle) : vehicle
    if (vehicle.reloadTimer === 0 && armedVehicle.reloadTimer > 0) {
      nextEventId = addEvent(events, nextEventId, 'reload-start', vehicle.id)
    }
    if (!input.fire) return armedVehicle
    const fired = tryFire(armedVehicle, projectiles, nextProjectileId, vehicles)
    projectiles = fired.projectiles
    if (armedVehicle.reloadTimer === 0 && fired.vehicle.reloadTimer > 0) {
      nextEventId = addEvent(events, nextEventId, 'reload-start', vehicle.id)
    }
    if (fired.fired) {
      nextEventId = addEvent(events, nextEventId, 'fire', vehicle.id)
      nextProjectileId += 1
      if (vehicle.id === 'player') stats = { ...stats, shotsFired: stats.shotsFired + 1 }
    }
    armedVehicle = fired.vehicle
    return armedVehicle
  })

  projectiles = stepProjectiles(projectiles, safeDt)
  const combat = resolveProjectileHits(projectiles, vehicles)
  vehicles = combat.vehicles
  projectiles = combat.projectiles
  for (const hit of combat.hits) {
    nextEventId = addEvent(events, nextEventId, 'hit', hit.ownerId, hit.targetId)
    if (hit.ownerId === 'player') stats = { ...stats, hits: stats.hits + 1 }
    if (hit.stunned) {
      nextEventId = addEvent(events, nextEventId, 'driver-hit', hit.targetId, hit.ownerId)
    }
  }

  for (const vehicle of vehicles) {
    const before = race.progress[vehicle.id]
    const previousPosition = previousPositions.get(vehicle.id)
    const drivenEndpoint = drivenEndpoints.get(vehicle.id)
    if (!before || !previousPosition || !drivenEndpoint || invalidCheckpointSweeps.has(vehicle.id)) continue
    const checkpoint = before.nextCheckpoint
    if (!crossesCheckpointGate(state.track, previousPosition, drivenEndpoint, checkpoint)) continue
    let after = before
    if (state.classificationAuthority === 'local') {
      race = advanceCheckpoint(race, vehicle.id, checkpoint, race.elapsed)
      after = race.progress[vehicle.id] ?? before
      if (!before || !after || before === after) continue
      nextEventId = addEvent(events, nextEventId, 'checkpoint', vehicle.id, undefined, checkpoint)
    }
    const projection = projectToTrack(state.track, vehicle.position, vehicle.trackSampleIndex)
    const cleanBoost = getCleanCheckpointBoost({
      lateral: projection.lateral,
      trackWidth: state.track.width,
      speed: vehicle.speed,
    })
    if (cleanBoost > 0) {
      vehicles = vehicles.map((candidate) =>
        candidate.id === vehicle.id ? { ...candidate, boost: clamp(candidate.boost + cleanBoost, 0, 100) } : candidate,
      )
      nextEventId = addEvent(events, nextEventId, 'clean-sector', vehicle.id, undefined, cleanBoost)
    }
    if (state.classificationAuthority === 'local') {
      if (after.lap > before.lap) nextEventId = addEvent(events, nextEventId, 'lap', vehicle.id, undefined, after.lap)
      if (after.finishedAt !== null && before.finishedAt === null) {
        nextEventId = addEvent(events, nextEventId, 'finish', vehicle.id, undefined, race.finishOrder.length)
      }
    }
  }

  const segmentProgress = Object.fromEntries(
    vehicles.map((vehicle) => [
      vehicle.id,
      race.progress[vehicle.id]
        ? getCheckpointSegmentProgress(state.track, race.progress[vehicle.id], vehicle.trackSampleIndex)
        : 0,
    ]),
  )
  const ranking = state.classificationAuthority === 'local' ? rankRacers(race, segmentProgress) : state.ranking

  return {
    ...state,
    tick: state.tick + 1,
    time: state.time + safeDt,
    vehicles,
    projectiles,
    race,
    ranking,
    nextProjectileId,
    nextEventId,
    events,
    stats,
  }
}
